import omit from 'lodash/omit';
import {
    questionCustomisationFilterPrefix,
    QuestionCustomisationFilterType,
} from '../../../api/models/survey';
import {
    AttendeeSurvey,
    FormData,
    SurveyQuestion,
} from '../../../models/survey';
import { isCannedQuestion, isRequiredQuestion } from '../../../utils/surveys';
import { ephemeralFieldName } from '../../formContainers/connectCreateEphemeralField';
import {
    CANNED_QUESTION_COMPANY,
    CANNED_QUESTION_WORK_ADDRESS,
} from '../constants';
import { cleanUnregisteredQuestionAnswers } from './untypedUtils';

export const cleanUpDuplicateTaxesQuestions = (
    questionsList: Array<Pick<SurveyQuestion, 'name'>>,
) => {
    const duplicateQuestionList: Array<string> = [
        CANNED_QUESTION_WORK_ADDRESS,
        CANNED_QUESTION_COMPANY,
    ];

    return questionsList.filter(
        (element) => !duplicateQuestionList.includes(element.name),
    );
};

export const canUseIOSmessage = () => {
    const windowWithWebkit = window as unknown as {
        webkit?: { messageHandlers?: { ffi?: unknown } };
    };

    return (
        windowWithWebkit.webkit !== undefined &&
        windowWithWebkit.webkit.messageHandlers !== undefined &&
        windowWithWebkit.webkit.messageHandlers.ffi !== undefined
    );
};

export const canUseAndroidMessage = () => window.android !== undefined;

const isString = (variable: unknown): variable is string =>
    typeof variable === 'string';
const isObject = (variable: unknown): variable is Record<string, unknown> =>
    !!variable && typeof variable === 'object' && !Array.isArray(variable);

export const cleanFormData = (
    formData: FormData,
    survey: {
        questionsForBasicInfo: Array<SurveyQuestion>;
        questionsForBuyer: Array<SurveyQuestion>;
        questionsForAttendees: Array<AttendeeSurvey>;
    },
) =>
    omit(
        trimStringPropsObject(
            cleanUnregisteredQuestionAnswers(formData, survey),
        ),
        ephemeralFieldName,
    );

export const trimStringPropsObject = <Data extends Record<string, unknown>>(
    data: Data,
): Data =>
    Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
            if (isString(value)) {
                return [key, value.trim()];
            }

            return [
                key,
                isObject(value) ? trimStringPropsObject(value) : value,
            ];
        }),
    ) as Data;

export const getOptionalQuestions = <Question extends Array<SurveyQuestion>>(
    questions: Question,
) => questions.filter((question) => !isRequiredQuestion(question, questions));

export const getOptionalAndCannedQuestions = (
    questions: Array<SurveyQuestion>,
) =>
    questions.filter(
        (question) =>
            !isRequiredQuestion(question, questions) ||
            isCannedQuestion(question),
    );

export const getRequiredQuestions = (questions: Array<SurveyQuestion>) =>
    questions.filter((question) => isRequiredQuestion(question, questions));

export const getRequiredAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getRequiredQuestions(attendee.survey),
    }));

export const getOptionalAttendeesQuestions = <T extends AttendeeSurvey>(
    questionsForAttendees: Array<T>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getOptionalQuestions(attendee.survey),
    }));

export const getOptionalAndCannedAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getOptionalAndCannedQuestions(attendee.survey),
    }));

export const getQuestionsByCustomisation = ({
    isRequired,
    questionCustomizationFilter,
    questions,
}: {
    isRequired: boolean;
    questionCustomizationFilter: QuestionCustomisationFilterType;
    questions: Array<SurveyQuestion>;
}) =>
    questions.filter((singleQuestion) => {
        const isMatchingCustomizationFilter = singleQuestion.name.startsWith(
            questionCustomisationFilterPrefix[questionCustomizationFilter],
        );

        if (!isMatchingCustomizationFilter) {
            return false;
        }

        const singleQuestionIsRequired = isRequiredQuestion(
            singleQuestion,
            questions,
        );

        return isRequired === singleQuestionIsRequired;
    });
